import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@adop/ui';
import {
  CheckCircle2,
  ExternalLink,
  Globe,
  RefreshCw,
  Search,
  Star,
  XCircle,
} from 'lucide-react';
import type { ExternalOrderLinks } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { storefrontUrlFor } from '../../lib/storefront';
import { useAuth } from '../../lib/auth-context';
import { useAsync } from '../../lib/useAsync';
import { getLocation, updateLocationSeo } from '../../lib/data';
import {
  isValidExternalOrderLink,
  runVisibilityChecks,
  sanitizeExternalOrderLink,
  type VisibilityCheck,
} from '../../lib/visibility';
import { errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

const PRICE_RANGES = ['', '$', '$$', '$$$', '$$$$'] as const;

interface SeoForm {
  googleReviewUrl: string;
  uberEats: string;
  doordash: string;
  grubhub: string;
  cuisines: string;
  priceRange: string;
}

const EMPTY_FORM: SeoForm = {
  googleReviewUrl: '',
  uberEats: '',
  doordash: '',
  grubhub: '',
  cuisines: '',
  priceRange: '',
};

function parseCuisines(value: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of value.split(',')) {
    const cuisine = part.trim();
    if (cuisine.length === 0) continue;
    const key = cuisine.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(cuisine);
  }
  return result;
}

function linkValue(raw: string): string {
  return sanitizeExternalOrderLink(raw);
}

export function VisibilityPage() {
  const { location: workspaceLocation, canManage } = useWorkspace();
  const { refresh } = useAuth();
  const { toast } = useToast();

  const locationState = useAsync(
    () => (workspaceLocation ? getLocation(workspaceLocation.id) : Promise.resolve(null)),
    [workspaceLocation?.id],
  );
  const location = locationState.data;
  const baseUrl = location ? storefrontUrlFor(location.slug) : '';

  const [form, setForm] = useState<SeoForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checks, setChecks] = useState<VisibilityCheck[] | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!location) return;
    const links = (location.external_order_links ?? {}) as ExternalOrderLinks;
    setForm({
      googleReviewUrl: location.google_review_url ?? '',
      uberEats: links.uber_eats ?? '',
      doordash: links.doordash ?? '',
      grubhub: links.grubhub ?? '',
      cuisines: (location.cuisines ?? []).join(', '),
      priceRange: location.price_range ?? '',
    });
  }, [location]);

  const runChecks = useCallback(async () => {
    if (!location) return;
    setChecking(true);
    try {
      setChecks(await runVisibilityChecks(storefrontUrlFor(location.slug), location.name));
    } finally {
      setChecking(false);
    }
  }, [location]);

  useEffect(() => {
    void runChecks();
  }, [runChecks]);

  if (!workspaceLocation) {
    return <EmptyState icon={<Globe className="h-6 w-6" />} title="Select a location" />;
  }
  if (!canManage) {
    return (
      <EmptyState
        icon={<Globe className="h-6 w-6" />}
        title="Managers only"
        description="Ask an owner or manager to edit visibility settings."
      />
    );
  }
  if (locationState.loading || !location) {
    return <LoadingBlock />;
  }

  const save = async () => {
    const candidates: Array<{ key: keyof ExternalOrderLinks; value: string; label: string }> = [
      { key: 'uber_eats', value: linkValue(form.uberEats), label: 'Uber Eats' },
      { key: 'doordash', value: linkValue(form.doordash), label: 'DoorDash' },
      { key: 'grubhub', value: linkValue(form.grubhub), label: 'Grubhub' },
    ];
    const links: ExternalOrderLinks = {};
    for (const candidate of candidates) {
      if (candidate.value.length === 0) continue;
      if (!isValidExternalOrderLink(candidate.value)) {
        setError(`${candidate.label} link must be a full https:// URL.`);
        return;
      }
      links[candidate.key] = candidate.value;
    }

    setSaving(true);
    setError(null);
    try {
      await updateLocationSeo(location.id, {
        external_order_links: links,
        google_review_url: form.googleReviewUrl.trim() || null,
        cuisines: parseCuisines(form.cuisines),
        price_range: form.priceRange === '' ? null : form.priceRange,
      });
      toast({ title: 'Visibility settings saved', variant: 'success' });
      locationState.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const passed = (checks ?? []).filter((check) => check.status === 'pass').length;
  const total = (checks ?? []).length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Visibility"
        description="Help search engines and AI assistants find and understand this location."
        action={
          <a
            href={baseUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-300 px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <ExternalLink className="h-4 w-4" /> Open storefront
          </a>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>Search & AI details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Google review link"
            hint="Your Google Business Profile review URL. Guests see a “Leave a review” link after ordering."
            value={form.googleReviewUrl}
            placeholder="https://g.page/r/…/review"
            onChange={(event) => setForm({ ...form, googleReviewUrl: event.target.value })}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Uber Eats link"
              value={form.uberEats}
              placeholder="https://…"
              onChange={(event) => setForm({ ...form, uberEats: event.target.value })}
            />
            <Input
              label="DoorDash link"
              value={form.doordash}
              placeholder="https://…"
              onChange={(event) => setForm({ ...form, doordash: event.target.value })}
            />
            <Input
              label="Grubhub link"
              value={form.grubhub}
              placeholder="https://…"
              onChange={(event) => setForm({ ...form, grubhub: event.target.value })}
            />
          </div>
          <p className="text-xs text-zinc-500">
            These are shown as “Order on …” buttons when guests would rather use a delivery app. Leading
            spreadsheet characters are stripped so exported CSVs stay safe.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Cuisines"
              hint="Comma separated, e.g. Mexican, Tacos. Used in schema.org markup."
              value={form.cuisines}
              onChange={(event) => setForm({ ...form, cuisines: event.target.value })}
            />
            <Select
              label="Price range"
              value={form.priceRange}
              onChange={(event) => setForm({ ...form, priceRange: event.target.value })}
            >
              {PRICE_RANGES.map((range) => (
                <option key={range || 'none'} value={range}>
                  {range === '' ? 'Not set' : range}
                </option>
              ))}
            </Select>
          </div>
          <Button loading={saving} onClick={() => void save()}>
            Save visibility settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>What&apos;s live</CardTitle>
            <div className="flex items-center gap-2">
              {checks ? (
                <Badge variant={passed === total ? 'success' : 'warning'}>
                  {passed}/{total} passing
                </Badge>
              ) : null}
              <Button variant="outline" size="sm" loading={checking} onClick={() => void runChecks()}>
                <RefreshCw className="h-4 w-4" /> Re-check
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-zinc-600">
            We make your restaurant discoverable and machine-readable. We cannot guarantee whether or how AI
            assistants choose to recommend restaurants.
          </p>
          {!checks ? (
            <LoadingBlock label="Checking your live site…" />
          ) : (
            <ul className="space-y-2">
              {checks.map((check) => {
                const pass = check.status === 'pass';
                return (
                  <li
                    key={check.key}
                    className="flex items-start gap-3 rounded-lg border border-zinc-200 p-3 text-sm"
                  >
                    {pass ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    ) : (
                      <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-zinc-900">{check.label}</p>
                      <p className="text-zinc-500">{check.detail}</p>
                      {!pass ? <p className="mt-1 text-xs text-amber-700">Fix: {check.fix}</p> : null}
                    </div>
                    <a
                      href={check.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-zinc-600 underline"
                    >
                      <Search className="h-3.5 w-3.5" /> Inspect
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
          {form.googleReviewUrl.trim().length > 0 ? (
            <a
              href={form.googleReviewUrl.trim()}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900 underline"
            >
              <Star className="h-4 w-4" /> Open your Google review page
            </a>
          ) : (
            <p className="text-xs text-zinc-500">
              Add a Google review link above to let guests leave a review after they order.{' '}
              <Link to="/dashboard/payments" className="underline">
                Set up ordering
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
